import type { ErrorResponse } from '@panelvault/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

/** Same-origin in production, proxied by Vite in development. */
const BASE_URL = '/api';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    throw new ApiError(0, 'network_error', 'Server nicht erreichbar', cause);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const error = (payload as ErrorResponse | undefined)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'request_failed',
      error?.message ?? `Anfrage fehlgeschlagen (${response.status})`,
      error?.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/** Builds a query string, dropping empty values so filters stay tidy. */
export function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === 'all') continue;
    search.set(key, String(value));
  }

  const query = search.toString();
  return query ? `?${query}` : '';
}

/** Multipart upload; the browser sets the boundary itself. */
export async function uploadFile<T>(path: string, file: File): Promise<T> {
  const body = new FormData();
  body.append('file', file);

  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    body,
  });

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const error = (payload as ErrorResponse | undefined)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'upload_failed',
      error?.message ?? 'Upload fehlgeschlagen',
    );
  }

  return payload as T;
}

/** URL of a stored cover in the requested size. */
/**
 * A cover from a data source, fetched through our server: some sources turn
 * browsers away (the DNB answers them with a bot check), and the app does not
 * hotlink anyway.
 */
export function coverPreviewUrl(
  source: string,
  url: string | null | undefined,
): string | undefined {
  if (!url) return undefined;
  return `${BASE_URL}/sources/cover-preview?${new URLSearchParams({ source, url }).toString()}`;
}

export function imageUrl(
  id: string | null | undefined,
  variant: 'thumb' | 'medium' | 'large',
): string | undefined {
  return id ? `${BASE_URL}/images/${id}?variant=${variant}` : undefined;
}
